import { useEffect } from 'react'
import { useSignals } from '@preact/signals-react/runtime'
import { Button } from '@merels/ui'
import { JsonViewer } from '../../library/JsonViewer/JsonViewer'
import type { StateEditor as StateEditorDep } from '../../../../deps/debug/stateEditor/stateEditor.types'

export interface StateEditorProps {
  gameState: any
  ctx: any
  onApplyState: (state: { G?: any; ctx?: any }) => Promise<boolean>
  stateEditor: StateEditorDep
}

export function StateEditor({ gameState, ctx, onApplyState, stateEditor }: StateEditorProps) {
  useSignals()

  // Update editable JSON when game state or ctx changes
  useEffect(() => {
    stateEditor.resetToState(gameState, ctx)
  }, [gameState, ctx, stateEditor])

  const handleSubmit = async () => {
    stateEditor.setError(null)
    stateEditor.setIsApplying(true)
    
    try {
      const parsed = JSON.parse(stateEditor.editableJson.value)
      if (!parsed.G || !parsed.ctx) {
        stateEditor.setError('State must contain both G and ctx properties')
        return
      }
      
      const success = await onApplyState({ G: parsed.G, ctx: parsed.ctx })
      if (success) {
        stateEditor.setIsEditing(false)
      } else {
        stateEditor.setError('Failed to apply state')
      }
    } catch (e) {
      stateEditor.setError('Invalid JSON: ' + (e as Error).message)
    } finally {
      stateEditor.setIsApplying(false)
    }
  }

  const handleCancel = () => {
    stateEditor.cancelEditing()
    stateEditor.resetToState(gameState, ctx)
  }

  if (!gameState && !ctx) {
    return (
      <div className="p-4 text-sm text-muted text-center">
        No active game
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Error message */}
      {stateEditor.error.value && (
        <div className="p-2 bg-danger-bg/20 border-2 border-danger-bg/40 text-danger text-sm flex-shrink-0">
          {stateEditor.error.value}
        </div>
      )}

      <div className="flex-1 overflow-auto p-4">
        {stateEditor.isEditing.value ? (
          <div className="space-y-2 h-full flex flex-col">
            <textarea
              value={stateEditor.editableJson.value}
              onChange={(e) => {
                stateEditor.setEditableJson(e.target.value)
              }}
              className="w-full p-2 border rounded bg-background text-primary font-mono text-xs flex-1 min-h-[200px]"
              data-testid="state-editor-textarea"
            />
            <div className="flex gap-2 flex-shrink-0">
              <Button
                onClick={handleSubmit}
                disabled={stateEditor.isApplying.value}
                size="sm"
                data-testid="state-editor-submit"
              >
                {stateEditor.isApplying.value ? 'Applying...' : 'Submit'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div 
              onClick={() => stateEditor.startEditing()}
              className="cursor-pointer"
              data-testid="state-viewer-clickable"
            >
              <JsonViewer data={{ G: gameState, ctx: ctx }} />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => stateEditor.startEditing()}
              className="w-full"
              data-testid="state-editor-edit-button"
            >
              Edit State
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

